// slices/chat/chatSlice.ts
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { logout } from '../auth/authSlice';
import { decideSeq } from '../../services/chatStream/seqTracker';
import type { ChatStreamFailure } from '../../services/chatStream/chatStreamClient';
import type { ChatActionResult } from '../../services/chatActions';
import type { ChatMessageRating } from '../../services/chatRatings';
import type {
  ChatActionDetail,
  ChatActionUndo,
  ChatBlockPlacement,
  ChatContextLabel,
  ChatCitation,
  ChatDailyLimits,
  ChatFollowup,
  ChatNavigationTarget,
  ChatStreamEvent,
} from '../../types/chatStream';
import type {
  ChatTurnRequestedPayload,
  ChatStreamEventPayload,
  ChatHydratedMessage,
  ChatHydratedConversation,
  ChatBlockMessageAddedPayload,
} from './chatSlicePayloads';

export type {
  ChatTurnRequestedPayload,
  ChatStreamEventPayload,
  ChatHydratedBlock,
  ChatHydratedMessage,
  ChatHydratedConversation,
  ChatBlockMessageAddedPayload,
} from './chatSlicePayloads';

export type ChatMessageRole = 'user' | 'assistant';

/**
 * An attachment shown on a message. Live uploads always have storage metadata;
 * old conversation reads can have only an id, MIME type, and signed preview.
 */
export interface ChatMessageAttachment {
  id?: string;
  /** Documents are owner-scoped server ids; images omit this field. */
  documentId?: string;
  kind?: 'image' | 'document';
  fileName?: string;
  pageCount?: number | null;
  key?: string;
  mimeType: string;
  sizeBytes?: number;
  previewUrl?: string | null;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  role: ChatMessageRole;
  text: string;
  /** Client turn that produced this message. Null for hydrated history. */
  turnId: string | null;
  blockIds: string[];
  citations: ChatCitation[];
  attachments: ChatMessageAttachment[];
  /** Safe study-item snapshot for this user message, when its turn was anchored. */
  contextLabel?: ChatContextLabel;
  /** Prepared document ids retained for a faithful retry before history reload. */
  documentIds?: string[];
  /** Message id this one regenerates, for retry and regenerate linkage. */
  regenerateOf: string | null;
  /** Server-assigned id, once the server tells us one. */
  serverId: string | null;
  /**
   * The student's thumbs on this answer (ORA-464), or null for unrated. Held
   * here rather than in the action bar's own state because the bar unmounts
   * whenever the drawer closes, and a rating the student can no longer see
   * they gave is worse than none. Assistant messages only.
   */
  rating: ChatMessageRating;
  isStreaming: boolean;
  /**
   * Written by the old chat pipeline, so it carries inline `[A1]`/`[pN]`
   * markers and no structured citations. Only hydrated history can be legacy;
   * anything this client streams is not. Renderers strip the markers and show
   * the text plainly (there is no migration).
   */
  isLegacy: boolean;
}

export interface ChatBlock {
  id: string;
  conversationId: string;
  turnId: string;
  messageId: string;
  kind: string;
  placement: ChatBlockPlacement;
  payload: Record<string, unknown>;
  target: ChatNavigationTarget | null;
  /**
   * Restored from a legacy conversation's persisted UI state rather than
   * streamed by the agent runtime. Renderers draw those read-only: the archive
   * shows what the old pipeline served and may not answer or submit anything
   * new (ORA-448).
   */
  isLegacy: boolean;
}

export type ChatToolCallStatus = 'running' | 'ok' | 'error';

export interface ChatToolActivity {
  callId: string;
  name: string;
  args: Record<string, unknown>;
  status: ChatToolCallStatus;
  ms: number | null;
  summary: string | null;
}

/** What a confirmed action reported back, so the card can show its outcome. */
export interface ChatActionOutcome {
  summary: string | null;
  target: ChatNavigationTarget | null;
  /**
   * The action's own outcome, for actions that hand the client something to do
   * rather than somewhere to go (ORA-590) — the support prefill. It lives on
   * the proposal, not in the card, so a card that unmounts and comes back can
   * still offer to reopen the composer with the message it composed.
   */
  result: ChatActionResult | null;
}

export interface ChatActionProposal {
  actionId: string;
  turnId: string;
  name: string;
  args: Record<string, unknown>;
  summary: string;
  /**
   * Detail rows the server rendered for the card (ORA-607), preferred over
   * `args` when present. Optional: an older backend sends none.
   */
  details?: ChatActionDetail[];
  /** What the confirm button should say, when the server has an opinion. */
  confirmLabel?: string;
  /**
   * When this card arrived, on the client clock (so only ever compared with
   * itself). The card hides itself `ACTION_PROPOSAL_TTL_MS` after it, mirroring
   * the server's own window: the backend refuses a confirm past it, and a card
   * that looks live but cannot be confirmed is worse than no card (ORA-750).
   * Optional so a proposal built before this field existed still type-checks.
   */
  proposedAt?: number;
  /**
   * `confirmed` once the student has consented and the confirm endpoint has
   * executed the action. It lives here rather than in the card's own state
   * because the card unmounts — opening what the action created closes the
   * drawer — and a remounted card must not offer to run the same action again.
   */
  status: 'pending' | 'confirmed' | 'undone';
  outcome: ChatActionOutcome | null;
  /** Server-authored availability for the executed action's Undo affordance. */
  undo: ChatActionUndo | null;
  /** A terminal Undo refusal retained if the card unmounts and comes back. */
  undoFailure?: string | null;
  /**
   * Why the last attempt to run this action failed, for a card that is still
   * pending (ORA-750). The key's PRESENCE is what says it failed — `null` means
   * it failed and the server offered no sentence, and the card supplies its own.
   *
   * Only the server-side path writes this. A failure from tapping the card is
   * the card's own state for as long as it is mounted, which is long enough:
   * the student is looking at it. A failure the server reported mid-turn has to
   * survive the card unmounting, because nobody was necessarily looking.
   */
  failure?: string | null;
}

export type ChatTurnStatus =
  'requested' | 'streaming' | 'done' | 'error' | 'aborted' | 'failed';

export interface ChatTurnError {
  code: string;
  message: string | null;
  retryable: boolean;
}

export interface ChatTurn {
  /** Client-generated id. The slice keys turns by this so a retried request
   *  (token refresh, replay) never loses the turn already on screen. */
  id: string;
  serverTurnId: string | null;
  conversationId: string;
  status: ChatTurnStatus;
  statusLabel: string | null;
  /**
   * Every status label this turn has shown, oldest first, including the
   * current one. A turn can take several tool rounds (ORA-586), so the drawer
   * draws the finished steps above the live one rather than replacing the one
   * line and looking stuck (ORA-587). It leaves with the turn.
   */
  statusTrail: string[];
  /**
   * When the client sent this turn, for the elapsed readout on the activity
   * trail (ORA-597). Client clock, so it is only ever compared with itself.
   * Optional so a turn built before this field existed still type-checks.
   */
  startedAt?: number;
  userMessageId: string;
  assistantMessageId: string;
  regenerateOf: string | null;
  /**
   * Assistant messages this turn took out of the thread when it started, in
   * the order they stood in it. A regenerate replaces an answer rather than
   * adding one, and the conversation read hides the answer it replaced, so the
   * live thread has to hide it too or a bubble disappears on reload (ORA-459).
   * They go back if this turn ends up persisting nothing.
   */
  supersededMessageIds: string[];
  depth: 'deep' | null;
  /**
   * The app-context envelope this turn was sent with. Kept so a retry or a
   * regenerate replays the context the student typed against rather than
   * wherever they have wandered since (ORA-449).
   */
  envelope: Record<string, unknown> | null;
  protocol: number | null;
  model: string | null;
  /** Highest seq applied. Null until the turn's first event seeds the base. */
  lastSeq: number | null;
  /** True once an event arrived with a seq beyond the next expected one. */
  seqGapDetected: boolean;
  /**
   * `turn.start` named a conversation this turn did not have one for — so the
   * backend created that row for this turn, and owns whether it survives it.
   */
  createdConversation: boolean;
  toolCallIds: string[];
  toolCalls: Record<string, ChatToolActivity>;
  blockIds: string[];
  followups: ChatFollowup[];
  actionProposal: ChatActionProposal | null;
  error: ChatTurnError | null;
  traceId: string | null;
  steps: number | null;
  ms: number | null;
}

export interface ChatConversation {
  id: string;
  title: string | null;
  messageIds: string[];
  turnIds: string[];
  /** The turn currently streaming, if any. */
  activeTurnId: string | null;
  /**
   * Turn holding the conversation's action proposal — pending, or confirmed and
   * still showing its outcome.
   *
   * It used to be dropped the moment another turn opened, which is how 15 of 20
   * support cards in nine days died: the student replied "yes" / "send it" and
   * the button they were about to tap went with their own message (ORA-750).
   * The card now outlives the turn that proposed it, and the server closes it —
   * by auto-confirming an affirmation and reporting back on `action.resolved`,
   * by superseding it with a newer proposal of the same action, or by expiring
   * it — which is also who owns the TTL.
   */
  pendingActionTurnId: string | null;
}

export interface ChatState {
  conversations: Record<string, ChatConversation>;
  messages: Record<string, ChatMessage>;
  blocks: Record<string, ChatBlock>;
  turns: Record<string, ChatTurn>;
  activeConversationId: string | null;
  /**
   * The daily chat allowance. It belongs to the user, not to a conversation:
   * every turn on every conversation spends from the same budget, so any
   * `turn.start`/`turn.done` updates it and switching conversations must not
   * appear to hand the user a fresh allowance.
   */
  limits: ChatDailyLimits | null;
  /** Anchor key currently resolved by the shared page/drawer/document state. */
  threadTargetKey: string | null;
  /** Unique request id; stale resolver responses are ignored after logout. */
  threadResolutionId: string | null;
  isThreadResolving: boolean;
  /** A failed lookup is retried when its surface is opened again. */
  threadResolutionFailedKey: string | null;
  /** New Chat is pending its first turn for this exact anchor key. */
  newThreadKey: string | null;
}

export const initialState: ChatState = {
  conversations: {},
  messages: {},
  blocks: {},
  turns: {},
  activeConversationId: null,
  limits: null,
  threadTargetKey: null,
  threadResolutionId: null,
  isThreadResolving: false,
  threadResolutionFailedKey: null,
  newThreadKey: null,
};

const DRAFT_CONVERSATION_PREFIX = 'draft:';
/**
 * How many status steps one turn keeps. Three tool rounds emit well under
 * this; the cap only stops a misbehaving runtime from growing the trail
 * without bound.
 */
const MAX_STATUS_TRAIL = 8;

/**
 * How long a proposed action stays confirmable, mirroring `ora-be`'s own window.
 *
 * The backend refuses a confirm past it and answers `proposal_expired`, so a
 * card still on screen after this is a button that cannot work — and since
 * ORA-750 the card no longer leaves when the next turn opens, nothing else would
 * take it away. The client's copy is a mirror, not the authority: if the event
 * ever carries an `expiresAt`, read that instead.
 */
export const ACTION_PROPOSAL_TTL_MS = 10 * 60 * 1000;

const CONTEXT_LABELS: Record<ChatContextLabel['kind'], string> = {
  question: 'Question',
  flashcard: 'Flashcard',
  lecture: 'Document',
  video: 'Video',
  article: 'Article',
  exam: 'Exam',
};

/**
 * The pending user row has a generic, safe cue before `text.done` supplies
 * the server-vetted title. Never display anything except a recognised type
 * from the client-owned envelope.
 *
 * A detached turn shows no cue at all (ORA-662): the anchor still travels, for
 * thread keying, but the model is not given it — so saying "Question" on the
 * student's own message would claim something Ora cannot see.
 */
const contextLabelFromEnvelope = (
  envelope: Record<string, unknown> | null
): ChatContextLabel | undefined => {
  if (envelope?.anchorDetached === true) return undefined;

  const anchor = envelope?.anchor;
  if (!anchor || typeof anchor !== 'object' || Array.isArray(anchor))
    return undefined;

  const type = (anchor as Record<string, unknown>).type;
  if (
    typeof type !== 'string' ||
    !Object.prototype.hasOwnProperty.call(CONTEXT_LABELS, type)
  )
    return undefined;

  const kind = type as ChatContextLabel['kind'];
  const id = (anchor as Record<string, unknown>).id;
  return {
    kind,
    label: CONTEXT_LABELS[kind],
    ...(kind === 'lecture' && typeof id === 'string' ? { lectureId: id } : {}),
  };
};

/** A conversation that has no server id yet, so a turn can start before `turn.start`. */
export const draftConversationId = (clientTurnId: string): string =>
  `${DRAFT_CONVERSATION_PREFIX}${clientTurnId}`;

/**
 * True for an id the client invented rather than one the server assigned. The
 * send path must never put one of these on the wire: it names nothing the
 * backend knows about, and the whole point of a draft is that the next request
 * asks for a conversation to be created.
 */
export const isDraftConversationId = (id: string | null): boolean =>
  id !== null && id.startsWith(DRAFT_CONVERSATION_PREFIX);

const emptyConversation = (id: string): ChatConversation => ({
  id,
  title: null,
  messageIds: [],
  turnIds: [],
  activeTurnId: null,
  pendingActionTurnId: null,
});

const ensureConversation = (state: ChatState, id: string): ChatConversation => {
  const existing = state.conversations[id];
  if (existing) return existing;

  const created = emptyConversation(id);
  state.conversations[id] = created;
  return created;
};

const isTerminalStatus = (status: ChatTurnStatus): boolean =>
  status === 'done' ||
  status === 'error' ||
  status === 'aborted' ||
  status === 'failed';

const finishTurn = (state: ChatState, turn: ChatTurn) => {
  const assistant = state.messages[turn.assistantMessageId];
  if (assistant) assistant.isStreaming = false;

  const conversation = state.conversations[turn.conversationId];
  if (conversation && conversation.activeTurnId === turn.id) {
    conversation.activeTurnId = null;
  }
};

/**
 * Move a conversation from its draft id to the server id `turn.start` reported.
 * Every id that points back at the conversation moves with it, so nothing in
 * the slice keeps referring to the draft.
 */
const rekeyConversation = (state: ChatState, fromId: string, toId: string) => {
  if (fromId === toId) return;

  const draft = state.conversations[fromId];
  if (!draft) {
    ensureConversation(state, toId);
    return;
  }

  const target = state.conversations[toId];

  const merged: ChatConversation = target
    ? {
        ...target,
        messageIds: [...target.messageIds, ...draft.messageIds],
        turnIds: [...target.turnIds, ...draft.turnIds],
        activeTurnId: draft.activeTurnId ?? target.activeTurnId,
        title: target.title ?? draft.title,
      }
    : { ...draft, id: toId };

  delete state.conversations[fromId];
  state.conversations[toId] = merged;

  Object.values(state.messages).forEach((message) => {
    if (message.conversationId === fromId) message.conversationId = toId;
  });

  Object.values(state.blocks).forEach((block) => {
    if (block.conversationId === fromId) block.conversationId = toId;
  });

  Object.values(state.turns).forEach((turn) => {
    if (turn.conversationId === fromId) turn.conversationId = toId;
  });

  if (state.activeConversationId === fromId) {
    state.activeConversationId = toId;
  }
};

/**
 * Undo `turn.start`'s re-key when the conversation it named no longer exists.
 *
 * The backend creates the conversation row before the turn runs and deletes it
 * again when the turn ends terminally without persisting a message — which is
 * exactly what a brand-new conversation's first turn does when it errors. The
 * client, meanwhile, has already adopted the id off `turn.start`, so the next
 * send addresses a row that is gone and comes back `404 Conversation not
 * found`; the student is stuck until they start a new chat (ORA-459).
 *
 * So a terminally failed turn goes back to a client draft id when both of the
 * things that make a conversation disposable hold: this turn is the one the
 * row was created for (`turn.start` gave it an id it did not have), and no
 * assistant message in it has ever been persisted. The thread on screen is
 * untouched — the question and the failed answer keep their place — but the
 * next send carries no server id and creates a fresh conversation.
 *
 * A conversation the caller named, or one holding even a single persisted
 * assistant message, is a real row that outlives this turn and is left alone.
 *
 * The caller decides whether the turn left anything behind at all: an `error`
 * that names the partial answer it persisted, or that produced text, is not a
 * turn the backend cleaned up after.
 */
const revertUnpersistedConversationToDraft = (
  state: ChatState,
  turn: ChatTurn
) => {
  if (!turn.createdConversation) return;

  const conversation = state.conversations[turn.conversationId];
  if (!conversation || isDraftConversationId(conversation.id)) return;

  const hasPersistedAnswer = conversation.messageIds.some((messageId) => {
    const message = state.messages[messageId];
    return message?.role === 'assistant' && message.serverId !== null;
  });

  if (hasPersistedAnswer) return;

  rekeyConversation(state, conversation.id, draftConversationId(turn.id));
};

/**
 * Take the answers a regenerate replaces out of the thread.
 *
 * The conversation read drops a superseded answer (and the duplicate question
 * that came with it), so a thread that keeps showing the old bubble is a
 * thread with a message in it that vanishes on the next reload (ORA-459). The
 * messages themselves stay in the id map — the envelope replay still resolves
 * through them, and they go back if this turn persists nothing.
 */
const hideSupersededMessages = (
  state: ChatState,
  turn: ChatTurn,
  messageIds: string[]
) => {
  const conversation = state.conversations[turn.conversationId];
  if (!conversation) return;

  const hidden = new Set(
    messageIds.filter((id) => state.messages[id]?.role === 'assistant')
  );

  if (hidden.size === 0) return;

  // Recorded in thread order, which is the order they go back in.
  turn.supersededMessageIds = conversation.messageIds.filter((id) =>
    hidden.has(id)
  );

  conversation.messageIds = conversation.messageIds.filter(
    (id) => !hidden.has(id)
  );
};

/**
 * Put them back when the regenerate left nothing behind.
 *
 * A regenerate that failed, stalled or was stopped persisted no replacement,
 * so the answer it hid is still the conversation's answer on the server — and
 * hiding it here would lose it from the screen until a reload brought it back.
 * They return just above this turn's own answer, which is where they stood:
 * regenerate is only offered on the latest answer of a thread.
 */
const restoreSupersededMessages = (state: ChatState, turn: ChatTurn) => {
  if (turn.supersededMessageIds.length === 0) return;
  // The turn persisted a replacement after all; the hidden answers stay hidden.
  if (state.messages[turn.assistantMessageId]?.serverId) return;

  const conversation = state.conversations[turn.conversationId];
  if (!conversation) return;

  const restored = turn.supersededMessageIds.filter(
    (id) => state.messages[id] && !conversation.messageIds.includes(id)
  );

  turn.supersededMessageIds = [];
  if (restored.length === 0) return;

  const at = conversation.messageIds.indexOf(turn.assistantMessageId);
  const index = at === -1 ? conversation.messageIds.length : at;
  conversation.messageIds.splice(index, 0, ...restored);
};

/**
 * Put an executed action's outcome on its card. Shared by the tap path
 * (`actionProposalConfirmed`) and by the server settling it itself
 * (`action.resolved`, ORA-750) so an auto-confirmed action shows exactly what a
 * tapped one shows — the done state, with its Open affordance.
 *
 * The id has to match: a card can never be confirmed twice, and a resolution
 * for some other action must not land on the one on screen.
 */
const applyActionOutcome = (
  turn: ChatTurn | undefined,
  actionId: string,
  outcome: ChatActionOutcome
): void => {
  const proposal = turn?.actionProposal;
  if (!proposal || proposal.actionId !== actionId) return;

  proposal.status = 'confirmed';
  proposal.outcome = outcome;

  proposal.undo =
    outcome.result?.kind === 'plan_change' ||
    outcome.result?.kind === 'study_change'
      ? (outcome.result.undo ?? null)
      : null;
};

/**
 * Take one named card off the conversation.
 *
 * The turn is required: "whichever card the conversation points at" is not a
 * safe default now that two can be open at once, and a dismissal naming a card
 * this client does not have must clear nothing at all (ORA-750 review, SF3).
 */
const clearActionProposal = (
  conversation: ChatConversation,
  turn: ChatTurn | undefined
): void => {
  if (!turn) return;

  turn.actionProposal = null;

  if (conversation.pendingActionTurnId === turn.id) {
    conversation.pendingActionTurnId = null;
  }
};

/**
 * The turn whose card an `action.resolved` belongs to.
 *
 * The event arrives on the turn AFTER the one that proposed the action, so the
 * turn it has to change is never the streaming one. `proposedTurnId` names it,
 * as a SERVER turn id — but only as a hint: the client's own turn ids are
 * generated here, a conversation read back from history has rebuilt turns, and
 * the action id is what actually identifies the card. So the named turn is used
 * when it really is holding this proposal, and otherwise the card is found by
 * its action id within the conversation (ORA-750).
 */
const findProposingTurn = (
  state: ChatState,
  conversationId: string,
  actionId: string,
  proposedTurnId: string | undefined
): ChatTurn | undefined => {
  const named = proposedTurnId
    ? (state.turns[proposedTurnId] ??
      Object.values(state.turns).find(
        (candidate) => candidate.serverTurnId === proposedTurnId
      ))
    : undefined;

  // The conversation has to match too: turn ids are not scoped to a thread, and
  // a resolution streamed on one conversation must never reach another's card
  // (ORA-750 review round 2, SF4).
  if (
    named?.conversationId === conversationId &&
    named.actionProposal?.actionId === actionId
  ) {
    return named;
  }

  return Object.values(state.turns).find(
    (candidate) =>
      candidate.conversationId === conversationId &&
      candidate.actionProposal?.actionId === actionId
  );
};

/**
 * Apply one stream event to a turn. Shared by the single-event reducer and the
 * batched one so a coalesced flush behaves exactly like the events arriving
 * one at a time.
 */
const applyStreamEvent = (
  state: ChatState,
  turnId: string,
  event: ChatStreamEvent
) => {
  const turn = state.turns[turnId];
  if (!turn || isTerminalStatus(turn.status)) return;

  // Events belonging to a different server turn are not ours to apply.
  // `action.resolved` is no exception: it is about a card an EARLIER turn put on
  // screen, but the envelope still names the turn being streamed — the card it
  // changes is named by its own `proposedTurnId` (ORA-750).
  if (turn.serverTurnId && turn.serverTurnId !== event.turnId) return;

  const decision = decideSeq(turn.lastSeq, event.seq);
  if (decision === 'duplicate') return;

  if (decision === 'gap') turn.seqGapDetected = true;

  turn.lastSeq = event.seq;
  if (!turn.serverTurnId) turn.serverTurnId = event.turnId;
  if (turn.status === 'requested') turn.status = 'streaming';

  const assistant = state.messages[turn.assistantMessageId];
  const conversation = state.conversations[turn.conversationId];

  switch (event.type) {
    case 'turn.start': {
      turn.protocol = event.protocol;
      turn.model = event.model;

      if (isDraftConversationId(turn.conversationId)) {
        turn.createdConversation = true;
      }

      rekeyConversation(state, turn.conversationId, event.conversationId);
      if (event.limits) state.limits = event.limits;
      break;
    }

    case 'status':
      turn.statusLabel = event.label;

      // A round can re-emit the label it is already showing; that is the same
      // step continuing, not a new one.
      if (turn.statusTrail[turn.statusTrail.length - 1] !== event.label) {
        turn.statusTrail.push(event.label);
        if (turn.statusTrail.length > MAX_STATUS_TRAIL)
          turn.statusTrail.shift();
      }

      break;

    case 'tool.call':
      if (!turn.toolCalls[event.callId]) turn.toolCallIds.push(event.callId);

      turn.toolCalls[event.callId] = {
        callId: event.callId,
        name: event.name,
        args: event.args,
        status: 'running',
        ms: null,
        summary: null,
      };

      break;

    case 'tool.result': {
      const call = turn.toolCalls[event.callId];
      if (call) {
        call.status = event.ok ? 'ok' : 'error';
        call.ms = event.ms;
        call.summary = event.summary;
      }
      break;
    }

    case 'block': {
      state.blocks[event.blockId] = {
        id: event.blockId,
        conversationId: turn.conversationId,
        turnId: turn.id,
        messageId: turn.assistantMessageId,
        kind: event.kind,
        placement: event.placement,
        payload: event.payload,
        target: event.target ?? null,
        isLegacy: false,
      };

      if (!turn.blockIds.includes(event.blockId)) {
        turn.blockIds.push(event.blockId);
      }

      if (assistant && !assistant.blockIds.includes(event.blockId)) {
        assistant.blockIds.push(event.blockId);
      }
      break;
    }

    case 'text.delta':
      if (assistant) assistant.text += event.delta;
      break;

    case 'text.done':
      if (assistant) {
        assistant.text = event.text;
        assistant.citations = event.citations;
        /**
         * The answer is finished here, not at `turn.done`: the runtime still
         * has persistence and the title generation to do, and a reader was
         * left staring at raw markdown — table pipes, list dashes — for
         * seconds of it (ORA-459). The turn stays open for everything else
         * `turn.done` carries: limits, trace id, message id.
         */
        assistant.isStreaming = false;
      }
      if (event.contextLabel) {
        const userMessage = state.messages[turn.userMessageId];
        if (userMessage?.role === 'user') {
          userMessage.contextLabel = event.contextLabel;
        }
      }
      break;

    case 'followups':
      turn.followups = event.items;
      break;

    case 'action.proposed': {
      /**
       * A newer proposal of the same action supersedes the older one (ORA-750).
       * Cards no longer expire when a turn opens, so this is how the server
       * closes one it has replaced — "make it 40 questions instead" proposes a
       * second `create_qbank_block`, and two live cards for the same action
       * would let the student run both.
       */
      Object.values(state.turns).forEach((candidate) => {
        if (
          candidate.id !== turn.id &&
          candidate.conversationId === turn.conversationId &&
          candidate.actionProposal?.name === event.name
        ) {
          candidate.actionProposal = null;
        }
      });

      turn.actionProposal = {
        actionId: event.actionId,
        turnId: turn.id,
        name: event.name,
        args: event.args,
        summary: event.summary,
        details: event.details,
        confirmLabel: event.confirmLabel,
        proposedAt: Date.now(),
        status: 'pending',
        outcome: null,
        undo: null,
      };

      if (conversation) conversation.pendingActionTurnId = turn.id;
      break;
    }

    case 'action.resolved': {
      if (!conversation) break;

      const proposing = findProposingTurn(
        state,
        conversation.id,
        event.actionId,
        event.proposedTurnId
      );

      const proposal = proposing?.actionProposal;

      if (!proposing || !proposal || proposal.actionId !== event.actionId) {
        break;
      }

      if (event.status === 'EXECUTED') {
        // The student said "yes" and the server ran it, so the card shows the
        // same done state a tap would have left behind. A server that sent no
        // summary still gets the confirmed state: the card's own fallback copy
        // ("Done.") is truer than a button that offers to run it again.
        applyActionOutcome(proposing, event.actionId, {
          summary: event.summary ?? null,
          target: event.target ?? null,
          result: event.result ?? null,
        });

        // The card is the conversation's again, in case some other proposal
        // has since taken the pointer.
        conversation.pendingActionTurnId = proposing.id;
        break;
      }

      if (event.status === 'FAILED') {
        /**
         * Exactly where a failed tap leaves the card: still pending, with the
         * server's sentence above a confirm button that now reads "Try Again".
         * An action confirmed in words can fail the way a tapped one can, and
         * the student's recourse is the same — press it again. The sentence
         * lives on the proposal rather than in the card because the card
         * unmounts whenever the drawer closes.
         */
        proposal.failure = event.error ?? null;
        conversation.pendingActionTurnId = proposing.id;
        break;
      }

      /**
       * Dismissed or expired: there is nothing left to tap, and a card with no
       * outcome would only invite a confirm the server will refuse.
       *
       * Unless it already ran. A tap and the runtime's own reading of the next
       * message can both land on the same card, and the tap wins on the facts —
       * the action happened. Clearing the receipt then would take away the
       * student's way into what was built (round 2, SF5).
       */
      if (proposal.status === 'confirmed') break;

      clearActionProposal(conversation, proposing);
      break;
    }

    case 'title':
      if (conversation) conversation.title = event.title;
      break;

    case 'turn.done':
      turn.status = 'done';
      turn.traceId = event.traceId;
      turn.steps = event.steps;
      turn.ms = event.ms;
      turn.statusLabel = null;
      // The trail stays: a finished turn folds it into the "Thought for 4s"
      // summary the student can open again (ORA-597). A turn that ended badly
      // still drops it — `ChatTurnNotice` owns that story.
      if (event.limits) state.limits = event.limits;
      if (assistant && event.messageId) assistant.serverId = event.messageId;
      finishTurn(state, turn);
      break;

    case 'error': {
      turn.status = 'error';
      turn.statusLabel = null;
      turn.statusTrail = [];

      turn.error = {
        code: event.code,
        message: event.message ?? null,
        retryable: event.retryable,
      };

      // Keep whatever text we already streamed; `partial` only fills in
      // when it carries more than the deltas managed to deliver.
      if (
        assistant &&
        event.partial &&
        event.partial.length > assistant.text.length
      ) {
        assistant.text = event.partial;
      }

      /**
       * A turn that produced text persists that partial answer, and the error
       * names it (ORA-459). Naming it here is what makes a retry or a
       * regenerate of a failed answer address the row the runtime actually
       * wrote, rather than a client id the backend has never heard of.
       */
      if (assistant && event.messageId) assistant.serverId = event.messageId;

      finishTurn(state, turn);

      /**
       * Only a turn that left nothing behind takes its conversation with it.
       * The runtime persists whenever it produced text, so an answer that
       * streamed and then failed lives in a conversation that is still there;
       * re-keying it to a draft would orphan that answer and open a second
       * conversation on the next send.
       */
      const leftNothingBehind =
        !event.messageId && (assistant ? assistant.text.length === 0 : true);

      if (leftNothingBehind) revertUnpersistedConversationToDraft(state, turn);
      restoreSupersededMessages(state, turn);
      break;
    }

    case 'ping':
      break;
  }
};

/**
 * The finished turn behind a restored answer (ORA-604, ORA-606).
 *
 * History has no turns of its own — the client ids the stream used are gone —
 * so a message that carries a trail or its chips gets a turn keyed by the
 * MESSAGE id, which is the key `ChatMessageItem` looks a turn up by. It is
 * built `done` and empty of everything a live turn accumulates: what the
 * student can see of it is exactly what the server persisted.
 */
const hydratedTurn = (
  conversationId: string,
  message: ChatHydratedMessage,
  userMessageId: string
): ChatTurn => ({
  id: message.id,
  serverTurnId: null,
  conversationId,
  status: 'done',
  statusLabel: null,
  statusTrail: message.activity ? [...message.activity.statusTrail] : [],
  // No `startedAt`: the turn ran in another session, so there is no client
  // clock to measure it against — the persisted duration is the only one.
  userMessageId,
  assistantMessageId: message.id,
  regenerateOf: null,
  supersededMessageIds: [],
  depth: null,
  envelope: null,
  protocol: null,
  model: null,
  lastSeq: null,
  seqGapDetected: false,
  createdConversation: false,
  toolCallIds: [],
  toolCalls: {},
  blockIds: (message.blocks ?? []).map((block) => block.id),
  followups: message.followups ?? [],
  actionProposal: message.actionProposal
    ? { ...message.actionProposal, turnId: message.id }
    : null,
  error: null,
  traceId: null,
  steps: null,
  // A persisted zero means the server had no duration to record, which is not
  // the same as "it took no time": the summary then counts steps alone.
  ms: message.activity && message.activity.ms > 0 ? message.activity.ms : null,
});

/** True when a turn is one this reducer rebuilt from history, not a live one. */
const isHydratedTurn = (turn: ChatTurn | undefined, messageId: string) =>
  turn !== undefined &&
  turn.id === messageId &&
  turn.assistantMessageId === messageId;

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    threadResolutionStarted: (
      state,
      action: PayloadAction<{ key: string; resolutionId: string }>
    ) => {
      state.threadTargetKey = action.payload.key;
      state.threadResolutionId = action.payload.resolutionId;
      state.isThreadResolving = true;
      state.threadResolutionFailedKey = null;
      state.newThreadKey = null;
      state.activeConversationId = null;
    },

    /**
     * A history choice owns the same shared navigation token as an anchor
     * lookup. Keeping the current anchor key prevents a mounted resolver from
     * immediately undoing the explicit choice; changing anchors still starts a
     * new lookup and invalidates this read.
     */
    threadHistorySelectionStarted: (
      state,
      action: PayloadAction<{
        key: string;
        resolutionId: string;
        conversationId: string;
      }>
    ) => {
      state.threadTargetKey = action.payload.key;
      state.threadResolutionId = action.payload.resolutionId;
      state.isThreadResolving = true;
      state.threadResolutionFailedKey = null;
      state.newThreadKey = null;
      state.activeConversationId = action.payload.conversationId;
      ensureConversation(state, action.payload.conversationId);
    },

    threadResolutionFinished: (
      state,
      action: PayloadAction<{ key: string; resolutionId: string }>
    ) => {
      if (
        state.threadTargetKey !== action.payload.key ||
        state.threadResolutionId !== action.payload.resolutionId
      ) {
        return;
      }
      state.isThreadResolving = false;
    },

    threadResolutionFailed: (
      state,
      action: PayloadAction<{ key: string; resolutionId: string }>
    ) => {
      if (
        state.threadTargetKey !== action.payload.key ||
        state.threadResolutionId !== action.payload.resolutionId
      ) {
        return;
      }
      state.isThreadResolving = false;
      state.threadResolutionFailedKey = action.payload.key;
    },

    threadNewRequested: (state, action: PayloadAction<string>) => {
      state.threadTargetKey = action.payload;
      state.threadResolutionId = null;
      state.isThreadResolving = false;
      state.threadResolutionFailedKey = null;
      state.newThreadKey = action.payload;
      state.activeConversationId = null;
    },

    conversationActivated: (state, action: PayloadAction<string | null>) => {
      state.activeConversationId = action.payload;
      if (action.payload) ensureConversation(state, action.payload);
    },

    /**
     * Seed a conversation loaded from the server. History replaces what was
     * there, except for messages belonging to a turn that is still running —
     * a fetch landing mid-stream must not delete the answer being typed. Every
     * message the hydrate does drop is deleted outright, with its blocks, so
     * nothing is left orphaned in the id maps.
     */
    conversationHydrated: (
      state,
      action: PayloadAction<ChatHydratedConversation>
    ) => {
      const { id, title, messages, limits, legacy = false } = action.payload;
      const conversation = ensureConversation(state, id);
      const hydratedIds = messages.map((message) => message.id);

      const liveIds = conversation.messageIds.filter((messageId) => {
        if (hydratedIds.includes(messageId)) return false;

        const turnId = state.messages[messageId]?.turnId;
        const turn = turnId ? state.turns[turnId] : undefined;

        return turn !== undefined && !isTerminalStatus(turn.status);
      });

      const keptIds = new Set([...hydratedIds, ...liveIds]);

      conversation.messageIds.forEach((messageId) => {
        if (keptIds.has(messageId)) return;

        delete state.messages[messageId];

        // A turn this reducer rebuilt for that message goes with it; a live
        // turn is keyed by its own client id and is never touched here.
        if (isHydratedTurn(state.turns[messageId], messageId)) {
          delete state.turns[messageId];
        }

        Object.values(state.blocks).forEach((block) => {
          if (block.messageId === messageId) delete state.blocks[block.id];
        });
      });

      conversation.title = title;
      conversation.messageIds = [...hydratedIds, ...liveIds];
      if (limits !== undefined) state.limits = limits;

      /** Turns rebuilt from this read, in thread order. */
      const hydratedTurnIds: string[] = [];
      /** The question a rebuilt turn answered, for a retry of a restored row. */
      let lastUserMessageId = '';

      messages.forEach((message) => {
        const blocks = message.blocks ?? [];

        // Re-hydrating a message replaces its blocks, so drop whatever it had
        // before rather than leaving orphans behind in the block map.
        Object.values(state.blocks).forEach((block) => {
          if (block.messageId === message.id) delete state.blocks[block.id];
        });

        if (message.role === 'user') lastUserMessageId = message.id;

        /**
         * Only an answer the server sent a trail or chips for gets a turn.
         * Everything else stays turnless, which is what makes the activity
         * line and the follow-ups absent rather than empty.
         */
        const hasTurnRecord =
          message.role === 'assistant' &&
          (message.activity !== undefined ||
            (message.followups?.length ?? 0) > 0 ||
            message.actionProposal !== undefined);

        if (hasTurnRecord) {
          state.turns[message.id] = hydratedTurn(
            id,
            message,
            lastUserMessageId
          );

          hydratedTurnIds.push(message.id);
          if (message.actionProposal) {
            conversation.pendingActionTurnId = message.id;
          }
        } else if (isHydratedTurn(state.turns[message.id], message.id)) {
          // It had one on an earlier read and no longer does.
          delete state.turns[message.id];
        }

        state.messages[message.id] = {
          id: message.id,
          conversationId: id,
          role: message.role,
          text: message.text,
          turnId: hasTurnRecord ? message.id : null,
          blockIds: blocks.map((block) => block.id),
          citations: message.citations ?? [],
          attachments: message.attachments ?? [],
          ...(message.contextLabel
            ? { contextLabel: message.contextLabel }
            : {}),
          regenerateOf: null,
          serverId: message.id,
          rating: null,
          isStreaming: false,
          isLegacy: legacy,
        };

        blocks.forEach((block) => {
          state.blocks[block.id] = {
            id: block.id,
            conversationId: id,
            // Restored history belongs to no client turn; the empty id keeps
            // the field's type honest without inventing one.
            turnId: '',
            messageId: message.id,
            kind: block.kind,
            placement: block.placement ?? 'end',
            payload: block.payload,
            target: block.target ?? null,
            isLegacy: legacy,
          };
        });
      });

      // Rebuilt turns lead, then whatever live turns the conversation already
      // had — the same order the messages are in, so the last turn of the
      // thread is the last turn of the list. Registering them here is also
      // what makes `conversationRemoved` clean them up.
      conversation.turnIds = [
        ...hydratedTurnIds,
        ...conversation.turnIds.filter(
          (turnId) =>
            !hydratedTurnIds.includes(turnId) &&
            state.turns[turnId] !== undefined
        ),
      ];
    },

    /**
     * Open a turn locally, before the request goes out. Re-sending with an
     * existing user message id (retry, regenerate) reuses that message instead
     * of repeating it in the thread.
     */
    turnRequested: (state, action: PayloadAction<ChatTurnRequestedPayload>) => {
      const {
        turnId,
        userMessage,
        assistantMessageId,
        regenerateOf = null,
        supersedes = [],
        depth = null,
        envelope = null,
      } = action.payload;

      const conversationId =
        action.payload.conversationId ?? draftConversationId(turnId);

      const conversation = ensureConversation(state, conversationId);
      const pendingContextLabel = contextLabelFromEnvelope(envelope);

      if (!state.messages[userMessage.id]) {
        state.messages[userMessage.id] = {
          id: userMessage.id,
          conversationId,
          role: 'user',
          text: userMessage.text,
          turnId,
          blockIds: [],
          citations: [],
          attachments: userMessage.attachments ?? [],
          ...(pendingContextLabel ? { contextLabel: pendingContextLabel } : {}),
          ...(userMessage.documentIds?.length
            ? { documentIds: userMessage.documentIds }
            : {}),
          regenerateOf: null,
          serverId: null,
          rating: null,
          isStreaming: false,
          isLegacy: false,
        };

        conversation.messageIds.push(userMessage.id);
      }

      state.messages[assistantMessageId] = {
        id: assistantMessageId,
        conversationId,
        role: 'assistant',
        text: '',
        turnId,
        blockIds: [],
        citations: [],
        attachments: [],
        regenerateOf,
        serverId: null,
        rating: null,
        isStreaming: true,
        isLegacy: false,
      };

      if (!conversation.messageIds.includes(assistantMessageId)) {
        conversation.messageIds.push(assistantMessageId);
      }

      state.turns[turnId] = {
        id: turnId,
        serverTurnId: null,
        conversationId,
        status: 'requested',
        statusLabel: null,
        statusTrail: [],
        startedAt: Date.now(),
        userMessageId: userMessage.id,
        assistantMessageId,
        regenerateOf,
        supersededMessageIds: [],
        depth,
        envelope,
        protocol: null,
        model: null,
        lastSeq: null,
        seqGapDetected: false,
        createdConversation: false,
        toolCallIds: [],
        toolCalls: {},
        blockIds: [],
        followups: [],
        actionProposal: null,
        error: null,
        traceId: null,
        steps: null,
        ms: null,
      };

      /**
       * A PENDING action proposal deliberately survives this (ORA-750). It used
       * to be dropped here — both the conversation's pointer and the proposal on
       * the turn it came from — which meant the card vanished the instant the
       * student replied to it, and "yes" / "send it" is the most natural way to
       * answer a confirmation. The server closes a pending proposal itself: it
       * auto-confirms an affirmation and reports back on `action.resolved`,
       * supersedes it with a newer proposal of the same action, or expires it.
       *
       * A card that is already ANSWERED is a different thing: it is a receipt,
       * and a receipt pinned above the composer for the rest of the conversation
       * is clutter the student cannot get rid of. So asking the next question
       * puts it away.
       */
      conversation.turnIds.forEach((id) => {
        const candidate = state.turns[id];

        if (
          candidate?.actionProposal &&
          candidate.actionProposal.status !== 'pending'
        ) {
          candidate.actionProposal = null;

          if (conversation.pendingActionTurnId === id) {
            conversation.pendingActionTurnId = null;
          }
        }
      });

      conversation.turnIds.push(turnId);
      conversation.activeTurnId = turnId;
      state.activeConversationId = conversationId;

      // Last, so the answers this turn replaces leave the thread only once its
      // own answer is in it — the row it takes their place in.
      if (supersedes.length > 0) {
        hideSupersededMessages(state, state.turns[turnId], supersedes);
      }
    },

    streamEventReceived: (
      state,
      action: PayloadAction<ChatStreamEventPayload>
    ) => {
      applyStreamEvent(state, action.payload.turnId, action.payload.event);
    },

    /**
     * A run of events applied in one dispatch, in wire order.
     *
     * The stream client hands the thunk one `text.delta` per chunk and the
     * thread paints once per dispatch, so a fast answer used to cost a Redux
     * round trip and a layout read per token. The thunk buffers the deltas and
     * flushes them a frame at a time through here; a terminal event flushes the
     * buffer first, so ordering — and with it the seq cursor — is unchanged
     * (ORA-459).
     */
    streamEventsReceived: (
      state,
      action: PayloadAction<{ turnId: string; events: ChatStreamEvent[] }>
    ) => {
      action.payload.events.forEach((event) => {
        applyStreamEvent(state, action.payload.turnId, event);
      });
    },
    followupsReceived: (
      state,
      action: PayloadAction<{
        turnId: string;
        messageId: string;
        items: ChatFollowup[];
      }>
    ) => {
      const turn = state.turns[action.payload.turnId];
      const assistant = turn ? state.messages[turn.assistantMessageId] : null;
      // A late response belongs only to the completed answer that requested
      // it. It must not populate a fresh draft after the student sends again.
      if (
        !turn ||
        turn.status !== 'done' ||
        assistant?.serverId !== action.payload.messageId
      ) {
        return;
      }
      turn.followups = action.payload.items;
    },

    /**
     * An event the client deliberately skipped (an unknown name from a newer
     * server). Nothing is applied, but the cursor moves so the next event is
     * not mistaken for a gap.
     */
    streamEventSkipped: (
      state,
      action: PayloadAction<{ turnId: string; seq: number }>
    ) => {
      const turn = state.turns[action.payload.turnId];
      if (!turn || isTerminalStatus(turn.status)) return;

      const decision = decideSeq(turn.lastSeq, action.payload.seq);
      if (decision === 'duplicate') return;

      if (decision === 'gap') turn.seqGapDetected = true;

      turn.lastSeq = action.payload.seq;
    },

    /** The transport gave out: HTTP, network, stall, or a closed stream. */
    streamFailed: (
      state,
      action: PayloadAction<{ turnId: string; failure: ChatStreamFailure }>
    ) => {
      const turn = state.turns[action.payload.turnId];
      if (!turn || isTerminalStatus(turn.status)) return;

      const { failure } = action.payload;

      turn.status = 'failed';
      turn.statusLabel = null;
      turn.statusTrail = [];

      turn.error = {
        code: failure.code,
        message: failure.message,
        retryable: failure.retryable,
      };

      /**
       * A daily-limit 429 never opens a stream, so the meter would otherwise
       * keep showing whatever the last turn reported and the student would be
       * offered a retry that cannot succeed. The numbers the server sent with
       * the refusal are the authority; a cap it did not restate stays as it is
       * (ORA-459).
       */
      if (failure.limits) {
        state.limits = {
          chatsRemaining: failure.limits.chatsRemaining,
          chatsLimit:
            failure.limits.chatsLimit ?? state.limits?.chatsLimit ?? null,
        };
      }

      finishTurn(state, turn);
      revertUnpersistedConversationToDraft(state, turn);
      restoreSupersededMessages(state, turn);
    },

    /**
     * Take a turn back out of the thread as though it had never opened
     * (ORA-750).
     *
     * For a turn the route refused before it ran: a message over the length cap
     * reached no model, so there is no answer to show, and a failed bubble under
     * the student's own paragraph offers a retry that must fail again — the only
     * fix is to edit the text, and the composer hands it back to them to do it.
     * So the question, the empty answer and the turn all leave, and anything the
     * turn hid on its way in comes back.
     *
     * Not a general-purpose eraser: every other ending keeps its turn, because
     * every other ending left something on screen worth keeping.
     */
    turnDiscarded: (state, action: PayloadAction<{ turnId: string }>) => {
      const turn = state.turns[action.payload.turnId];
      if (!turn) return;

      finishTurn(state, turn);
      restoreSupersededMessages(state, turn);
      // Before the messages go: this reads them to decide whether the backend
      // threw away the conversation it made for this turn.
      revertUnpersistedConversationToDraft(state, turn);

      const dropped = [turn.userMessageId, turn.assistantMessageId];
      // `revertUnpersistedConversationToDraft` may have re-keyed it.
      const conversation = state.conversations[turn.conversationId];

      if (conversation) {
        conversation.messageIds = conversation.messageIds.filter(
          (id) => !dropped.includes(id)
        );

        conversation.turnIds = conversation.turnIds.filter(
          (id) => id !== turn.id
        );

        if (conversation.pendingActionTurnId === turn.id) {
          conversation.pendingActionTurnId = null;
        }
      }

      turn.blockIds.forEach((id) => delete state.blocks[id]);
      dropped.forEach((id) => delete state.messages[id]);
      delete state.turns[turn.id];
    },

    /** The user stopped the turn. Partial text stays on screen. */
    turnAborted: (state, action: PayloadAction<{ turnId: string }>) => {
      const turn = state.turns[action.payload.turnId];
      if (!turn || isTerminalStatus(turn.status)) return;

      turn.status = 'aborted';
      turn.statusLabel = null;
      turn.statusTrail = [];
      finishTurn(state, turn);
      // A stopped first turn persists nothing either, so the backend deletes
      // the conversation it made for it exactly as it does on an error
      // (`chatTurnController`'s cleanup runs on every exit path). Same fix.
      revertUnpersistedConversationToDraft(state, turn);
      restoreSupersededMessages(state, turn);
    },

    /**
     * The action ran. The proposal stays on the conversation so its card can
     * show the outcome, but it can never be confirmed a second time — not by a
     * double click, and not by a card that unmounted and came back.
     */
    actionProposalConfirmed: (
      state,
      action: PayloadAction<{
        conversationId: string;
        actionId: string;
        outcome: ChatActionOutcome;
        /**
         * Put this card back before recording the outcome, for the one case
         * where it has already been taken off screen: the student pressed Skip
         * and the server answered that the action had already run (ORA-750
         * review round 2). They are owed the receipt, not a card that vanished.
         */
        reinstate?: ChatActionProposal;
      }>
    ) => {
      const conversation = state.conversations[action.payload.conversationId];
      if (!conversation) return;

      // By action id, never by "whatever the conversation points at": two cards
      // for different actions can be open at once, and the one the student
      // answered is the one that has to change (ORA-750 review, SF3).
      let turn = findProposingTurn(
        state,
        conversation.id,
        action.payload.actionId,
        undefined
      );

      const { reinstate } = action.payload;

      if (
        !turn &&
        reinstate &&
        reinstate.actionId === action.payload.actionId &&
        state.turns[reinstate.turnId]?.conversationId === conversation.id
      ) {
        turn = state.turns[reinstate.turnId];
        turn.actionProposal = reinstate;
      }

      if (!turn) return;

      applyActionOutcome(turn, action.payload.actionId, action.payload.outcome);

      // A record of the card most recently answered. Nothing decides what to
      // render from it — `selectPendingActionProposal` reads turn order — but
      // `turnRequested` and the tests use it to find the receipt.
      conversation.pendingActionTurnId = turn.id;
    },

    /** The compensation ran; keep the receipt, but never offer Undo again. */
    actionProposalUndone: (
      state,
      action: PayloadAction<{
        conversationId: string;
        actionId: string;
        undoneAt?: string;
      }>
    ) => {
      const conversation = state.conversations[action.payload.conversationId];
      if (!conversation) return;

      const turn = findProposingTurn(
        state,
        conversation.id,
        action.payload.actionId,
        undefined
      );

      const proposal = turn?.actionProposal;
      if (!turn || !proposal || proposal.status === 'pending') return;

      proposal.status = 'undone';
      if (proposal.undo) {
        proposal.undo.available = false;
        if (action.payload.undoneAt) {
          proposal.undo.undoneAt = action.payload.undoneAt;
        }
      }
      conversation.pendingActionTurnId = turn.id;
    },

    actionProposalUndoUnavailable: (
      state,
      action: PayloadAction<{
        conversationId: string;
        actionId: string;
        message: string | null;
      }>
    ) => {
      const conversation = state.conversations[action.payload.conversationId];
      if (!conversation) return;

      const proposal = findProposingTurn(
        state,
        conversation.id,
        action.payload.actionId,
        undefined
      )?.actionProposal;

      if (!proposal || proposal.status !== 'confirmed') return;

      if (proposal.undo) proposal.undo.available = false;
      proposal.undoFailure = action.payload.message;
    },

    actionProposalDismissed: (
      state,
      action: PayloadAction<{ conversationId: string; actionId: string }>
    ) => {
      const conversation = state.conversations[action.payload.conversationId];
      if (!conversation) return;

      clearActionProposal(
        conversation,
        findProposingTurn(
          state,
          conversation.id,
          action.payload.actionId,
          undefined
        )
      );
    },

    /**
     * The card's own 10-minute window closed (ORA-750). Local only: the server
     * keeps the same window and refuses a confirm past it, so there is nothing
     * to tell it — this just stops offering a button that cannot work.
     */
    actionProposalExpired: (
      state,
      action: PayloadAction<{ conversationId: string; actionId: string }>
    ) => {
      const conversation = state.conversations[action.payload.conversationId];
      if (!conversation) return;

      const turn = findProposingTurn(
        state,
        conversation.id,
        action.payload.actionId,
        undefined
      );

      // Only a card still waiting for an answer expires. One that already ran is
      // a receipt, and a receipt does not go stale.
      if (turn?.actionProposal?.status !== 'pending') return;

      clearActionProposal(conversation, turn);
    },

    conversationRemoved: (
      state,
      action: PayloadAction<{ conversationId: string }>
    ) => {
      const conversation = state.conversations[action.payload.conversationId];
      if (!conversation) return;

      conversation.messageIds.forEach((id) => delete state.messages[id]);
      conversation.turnIds.forEach((id) => delete state.turns[id]);

      Object.values(state.blocks).forEach((block) => {
        if (block.conversationId === conversation.id) {
          delete state.blocks[block.id];
        }
      });

      delete state.conversations[conversation.id];
      if (state.activeConversationId === conversation.id) {
        state.activeConversationId = null;
      }
    },

    /**
     * A source was added as a block (ORA-514). The server persisted a
     * block-only message right after the answer that cited it, so the thread
     * shows it there too, rather than at the bottom where it would read as a
     * new answer. Re-adding the same citation is idempotent server-side and
     * returns the same message; applying it again replaces that message's
     * blocks in place rather than repeating it.
     */
    blockMessageAdded: (
      state,
      action: PayloadAction<ChatBlockMessageAddedPayload>
    ) => {
      const { conversationId, afterMessageId, message, serverId } =
        action.payload;

      const conversation = state.conversations[conversationId];
      if (!conversation) return;

      const blocks = message.blocks ?? [];

      Object.values(state.blocks).forEach((block) => {
        if (block.messageId === message.id) delete state.blocks[block.id];
      });

      state.messages[message.id] = {
        id: message.id,
        conversationId,
        role: message.role,
        text: message.text,
        turnId: null,
        blockIds: blocks.map((block) => block.id),
        citations: message.citations ?? [],
        attachments: message.attachments ?? [],
        regenerateOf: null,
        serverId: serverId === undefined ? message.id : serverId,
        rating: null,
        isStreaming: false,
        isLegacy: false,
      };

      blocks.forEach((block) => {
        state.blocks[block.id] = {
          id: block.id,
          conversationId,
          turnId: '',
          messageId: message.id,
          kind: block.kind,
          placement: block.placement ?? 'end',
          payload: block.payload,
          target: block.target ?? null,
          isLegacy: false,
        };
      });

      if (conversation.messageIds.includes(message.id)) return;

      const anchor = conversation.messageIds.indexOf(afterMessageId);
      const index = anchor === -1 ? conversation.messageIds.length : anchor + 1;
      conversation.messageIds.splice(index, 0, message.id);
    },

    /**
     * The student rated an answer, or cleared the rating they gave (ORA-464).
     * Applied optimistically: the highlight is their own choice, not the
     * server's, and it must not wait on a round trip to appear.
     */
    messageRated: (
      state,
      action: PayloadAction<{ messageId: string; rating: ChatMessageRating }>
    ) => {
      const message = state.messages[action.payload.messageId];
      if (!message || message.role !== 'assistant') return;
      message.rating = action.payload.rating;
    },

    chatCleared: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState);
  },
});

export const {
  conversationActivated,
  threadHistorySelectionStarted,
  threadResolutionStarted,
  threadResolutionFinished,
  threadResolutionFailed,
  threadNewRequested,
  conversationHydrated,
  turnRequested,
  streamEventReceived,
  streamEventsReceived,
  followupsReceived,
  streamEventSkipped,
  streamFailed,
  turnDiscarded,
  turnAborted,
  actionProposalConfirmed,
  actionProposalUndone,
  actionProposalUndoUnavailable,
  actionProposalDismissed,
  actionProposalExpired,
  conversationRemoved,
  blockMessageAdded,
  messageRated,
  chatCleared,
} = chatSlice.actions;

export default chatSlice.reducer;
